import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { ArrowLeft, CircleAlert, Info } from 'lucide-react';

import styles from './auth.module.scss';

// Building blocks of the MyCanvas auth screens (docs/auth-flow.md). Values follow the
// "Auth 1-9" frames in design/mycanvas.pen, on the new design layer.

export const MIN_PASSWORD_LENGTH = 6; // CredentialProvider::Photolane::MIN_PASSWORD_LENGTH

export const AuthShell = ({ children }) => (
  <div className={styles.page}>
    <div className={styles.panel}>{children}</div>
  </div>
);

AuthShell.propTypes = {
  children: PropTypes.node,
};

export const Heading = ({ icon: IconComponent, tone, title, children }) => (
  <div className={styles.heading}>
    {IconComponent && (
      <span className={classNames(styles.badge, tone && styles[`badge--${tone}`])}>
        <IconComponent size={30} aria-hidden="true" />
      </span>
    )}
    {/* Focused after a mode switch so screen readers announce the new step. */}
    <h1 className={styles.title} tabIndex={-1}>
      {title}
    </h1>
    {children && <p className={styles.lead}>{children}</p>}
  </div>
);

Heading.propTypes = {
  icon: PropTypes.elementType,
  tone: PropTypes.oneOf(['warning', 'success']),
  title: PropTypes.node.isRequired,
  children: PropTypes.node,
};

export const BackLink = ({ onClick, children }) => (
  <button type="button" className={styles.back} onClick={onClick}>
    <ArrowLeft size={20} aria-hidden="true" />
    {children}
  </button>
);

BackLink.propTypes = {
  onClick: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
};

export const TextLink = ({ onClick, children }) => (
  <button type="button" className={styles.link} onClick={onClick}>
    {children}
  </button>
);

TextLink.propTypes = {
  onClick: PropTypes.func.isRequired,
  children: PropTypes.node,
};

export const Field = ({ id, label, children }) => (
  <div className={styles.field}>
    <label className={styles.label} htmlFor={id}>
      {label}
    </label>
    {children}
  </div>
);

Field.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.node.isRequired,
  children: PropTypes.node,
};

export const EmailInput = ({ invalid, errorId, ...props }) => (
  <input
    type="email"
    autoComplete="email"
    inputMode="email"
    autoCapitalize="none"
    spellCheck={false}
    className={classNames(styles.input, invalid && styles.invalid)}
    aria-invalid={invalid || undefined}
    aria-describedby={invalid ? errorId : undefined}
    {...props}
  />
);

EmailInput.propTypes = {
  invalid: PropTypes.bool,
  errorId: PropTypes.string,
};

export const PasswordInput = ({ invalid, errorId, hintId, ...props }) => {
  const [visible, setVisible] = useState(false);

  return (
    <div className={classNames(styles.input, styles.password, invalid && styles.invalid)}>
      <input
        type={visible ? 'text' : 'password'}
        aria-invalid={invalid || undefined}
        aria-describedby={[invalid && errorId, hintId].filter(Boolean).join(' ') || undefined}
        {...props}
        // Rollbar masks inputs only by type="password" or a name matching its scrubFields.
        // Show switches the type to text, so the name keeps the value masked.
        name="password"
      />
      <button
        type="button"
        className={styles.show}
        aria-label={visible ? 'Hide password' : 'Show password'}
        aria-pressed={visible}
        onClick={() => setVisible((value) => !value)}
      >
        {visible ? 'Hide' : 'Show'}
      </button>
    </div>
  );
};

PasswordInput.propTypes = {
  invalid: PropTypes.bool,
  errorId: PropTypes.string,
  hintId: PropTypes.string,
};

// Only shown where a new password is chosen. Sign in never checks the length.
export const PasswordHint = ({ id }) => (
  <p id={id} className={styles.hint}>
    At least {MIN_PASSWORD_LENGTH} characters
  </p>
);

PasswordHint.propTypes = {
  id: PropTypes.string.isRequired,
};

export const ErrorCallout = ({ id, title, children, actions }) => (
  <div id={id} className={styles.callout} role="alert">
    <CircleAlert size={20} className={styles.calloutIcon} aria-hidden="true" />
    <div className={styles.calloutContent}>
      <p className={styles.calloutTitle}>{title}</p>
      {children && <p>{children}</p>}
      {actions && <div className={styles.calloutActions}>{actions}</div>}
    </div>
  </div>
);

ErrorCallout.propTypes = {
  id: PropTypes.string,
  title: PropTypes.node.isRequired,
  children: PropTypes.node,
  actions: PropTypes.node,
};

export const Note = ({ title, children }) => (
  <div className={styles.note}>
    <Info size={20} className={styles.noteIcon} aria-hidden="true" />
    <div className={styles.calloutContent}>
      {title && <p className={styles.noteTitle}>{title}</p>}
      <p>{children}</p>
    </div>
  </div>
);

Note.propTypes = {
  title: PropTypes.node,
  children: PropTypes.node,
};

export const Divider = () => (
  <div className={styles.divider} aria-hidden="true">
    <span>Or</span>
  </div>
);
